import React, { useState, useMemo, useCallback } from "react";
import {
  Button,
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Row,
  Card,
  Col,
  CardBody,
  CardHeader,
} from "reactstrap";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import * as moment from "moment";

import DeleteModal from "../../../Components/Common/DeleteModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  createTableSearchParams,
  parseTableFilters,
  parseTableSearch,
  parseTableSort,
} from "common/lib/tableQueryParams";
import { normalize } from "common/lib/normalize";
import {
  deletePseaAssessment,
  getPseaAssessments,
} from "common/services/psea-assessments.service";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { usePseaCreator } from "./usePseaCreator";
import { PSEA_CREATE_CHECKS, PSEA_UPDATE_CHECKS } from "./usePseaPermissions";
import PSEARatingBadge from "./PSEARatingBadge";
import { AgencyFilter, OverallRatingFilter } from "./PSEATableFilters";

const PSEA_DELETE_CHECKS = [{ resource: "psea-assessment", action: "delete" }];

type PSEAFilterValue = {
  agency: any;
  overallRating: any;
};

const defaultFilterValue: PSEAFilterValue = {
  agency: null,
  overallRating: null,
};

const PSEAAssessments = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "PSEA Assessments");

  // Only used to word the empty state; the Add button itself is gated below.
  const { canCreate } = usePseaCreator();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 20,
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1,
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage - 1 }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );
  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [loading, setLoading] = useState(false);
  // Guards the empty state from flashing before the first fetch resolves.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);

  const [searchValue, setSearchValue] = useState(
    parseTableSearch(searchParams)
  );
  const [filterValue, setFilterValue] = useState<PSEAFilterValue>(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: PSEAFilterValue;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      const andConditions: any[] = [];

      if (filterValue?.agency?.value) {
        andConditions.push({ agency: { id: filterValue.agency.value } });
      }

      if (filterValue?.overallRating?.value) {
        andConditions.push({
          overallRating: { $eq: filterValue.overallRating.value },
        });
      }

      andConditions.push({
        $or: [
          { title: { $containsi: searchValue } },
          {
            contractor: {
              contractorName: { $containsi: searchValue },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: { page, pageSize: pagination.pageSize },
          sort: sortObj,
          populate: ["contractor", "agency"],
          filters: { $and: andConditions },
        };
        const response: any = await getPseaAssessments(apiParams);
        setData(normalize(response.data));
        setMeta(response.meta);
      } catch (err: any) {
        toast.error("Error while fetching data", { autoClose: 1000 });
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted,
      searchValue,
      filterValue,
    });
  }, [onPageChange, pagination.pageIndex, searchValue, sorted, filterValue]);

  const onClickDelete = useCallback((record: any) => {
    setSelectedRecord(record);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deletePseaAssessment(selectedRecord);
        fetchUpdatedData();
        setDeleteModal(false);
        toast.success("PSEA assessment deleted successfully");
      } catch (err: any) {
        toast.error("Error while deleting PSEA assessment");
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });

    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => deletePseaAssessment({ id }))
        );
        toast.success("PSEA assessments deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (err: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, PSEA_DELETE_CHECKS),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => (
          <input
            type="checkbox"
            className="tableCheckbox form-check-input"
            value={cell.getValue()}
            onChange={() => onSelectCheckbox()}
          />
        ),
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Contractor Assessed",
        accessorKey: "contractor.contractorName",
        id: "contractor.contractorName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const name = cell.getValue() || "-";
          const acronym = rowData?.contractor?.acronym;
          return (
            <div
              className="d-flex align-items-center"
              style={{ whiteSpace: "pre-wrap", minWidth: "300px" }}
            >
              <div className="flex-shrink-0 me-3">
                <div className="avatar-sm">
                  <div
                    className="avatar-title border bg-light text-primary rounded fs-24"
                    title="PSEA assessment record"
                  >
                    <i className="ri-file-pdf-line"></i>
                  </div>
                </div>
              </div>
              <div className="flex-grow-1">
                <Permission
                  alternative={<span className="fw-medium">{name}</span>}
                  action="findOne"
                  resource="psea-assessment"
                >
                  <Link
                    to={`/assessments/psea-assessments/${rowData.id}`}
                    className="fw-medium link-primary"
                  >
                    {name}
                  </Link>
                </Permission>
                <small className="text-muted d-block">
                  PSEA Assessment
                  {acronym ? ` · ${acronym}` : ""}
                </small>
              </div>
            </div>
          );
        },
      },
      {
        header: "Assessing Agency",
        accessorKey: "agency.name",
        id: "agency.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const agency = cell.row.original?.agency;
          if (!agency?.name) return <span className="text-muted">-</span>;

          return (
            // The full name rides along as a tooltip so the row stays on a
            // single line under `table-nowrap`.
            <span className="fw-medium" title={agency?.fullName || agency.name}>
              {agency.name}
            </span>
          );
        },
      },
      {
        header: "Overall Rating",
        accessorKey: "overallRating",
        id: "overallRating",
        enableColumnFilter: false,
        cell: (cell: any) => <PSEARatingBadge rating={cell.getValue()} />,
      },
      {
        header: "Date of Assessment",
        accessorKey: "dateOfAssessment",
        id: "dateOfAssessment",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const val = cell.getValue();
          return <>{val ? moment(new Date(val)).format("DD MMM Y") : "-"}</>;
        },
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        id: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const val = cell.getValue();
          if (!val) return <>-</>;
          return (
            <div>
              <span className="d-block">
                {moment(new Date(val)).format("DD MMM Y")}
              </span>
              <small className="text-muted">
                {moment(new Date(val)).fromNow()}
              </small>
            </div>
          );
        },
      },
      {
        header: "Action",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission action="findOne" resource="psea-assessment">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/assessments/psea-assessments/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>
                <Permissions checks={PSEA_UPDATE_CHECKS}>
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/assessments/psea-assessments/${rowData.id}/edit`
                      );
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permissions>
                <Permission action="delete" resource="psea-assessment">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  const isEmpty = hasLoaded && !loading && (data || []).length === 0;
  const hasActiveFilters = Boolean(
    searchValue ||
      filterValue?.agency?.value ||
      filterValue?.overallRating?.value
  );

  document.title = "PSEA Assessments | CIMS";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="PSEA Assessments"
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            { title: "Assessments", link: "/assessments/psea-assessments" },
          ]}
        />

        <DeleteModal
          show={deleteMultipleModal}
          onDeleteClick={() => handleDeleteMultiple()}
          onCloseClick={() => setDeleteMultipleModal(false)}
          loading={deleting}
        />
        <Permissions checks={PSEA_DELETE_CHECKS}>
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="PSEA assessment"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>

        <Row>
          <Col xl={12} lg={12}>
            <Card>
              <CardHeader>
                <Row className="align-items-center g-3">
                  <Col md>
                    <h5 className="card-title mb-1 d-flex align-items-center gap-2">
                      PSEA Assessments
                      {hasLoaded && (
                        <span className="badge bg-primary-subtle text-primary rounded-pill fs-11">
                          {meta?.pagination?.total || 0}
                        </span>
                      )}
                    </h5>
                    <p className="text-muted mb-0">
                      Protection from Sexual Exploitation and Abuse (PSEA)
                      assessments conducted for contractors.
                    </p>
                  </Col>
                  <Permissions checks={PSEA_CREATE_CHECKS}>
                    <Col md="auto">
                      <Link
                        to="/assessments/psea-assessments/new"
                        className="btn btn-primary"
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add PSEA Assessment
                      </Link>
                    </Col>
                  </Permissions>
                </Row>
              </CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col lg={5} md={12}>
                    <label
                      htmlFor="search-bar-0"
                      className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                    >
                      Search
                    </label>
                    <div className="search-box">
                      <input
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setPage(1);
                        }}
                        id="search-bar-0"
                        type="text"
                        className="form-control search"
                        placeholder="Search by title or contractor"
                        value={searchValue || ""}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                      {searchValue ? (
                        <button
                          type="button"
                          aria-label="Clear search"
                          title="Clear search"
                          className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
                          onClick={() => {
                            setSearchValue("");
                            setPage(1);
                          }}
                        >
                          <i className="ri-close-circle-fill fs-16"></i>
                        </button>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={4} md={6}>
                    <label
                      htmlFor="agencyFilter"
                      className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                    >
                      Assessing Agency
                    </label>
                    <AgencyFilter
                      value={filterValue?.agency}
                      onChange={(value: any) => {
                        setFilterValue({ ...filterValue, agency: value });
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    <label
                      htmlFor="overallRatingFilter"
                      className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                    >
                      Overall Rating
                    </label>
                    <OverallRatingFilter
                      value={filterValue?.overallRating}
                      onChange={(value: any) => {
                        setFilterValue({
                          ...filterValue,
                          overallRating: value,
                        });
                        setPage(1);
                      }}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                {/*
                  The table stays mounted even when empty: its effect is what
                  drives fetching on search/filter/sort changes.
                */}
                <div className={isEmpty ? "d-none" : ""}>
                  <TableContainer
                    loading={loading}
                    columns={columns}
                    data={data || []}
                    pagination={pagination}
                    setPagination={setPagination}
                    sorted={sorted}
                    setSorted={setSorted}
                    onPageChange={onPageChange}
                    FilterSection={() => {}}
                    totalRows={meta?.pagination?.total || 0}
                    totalPages={meta?.pagination?.pageCount || 0}
                    searchValue={searchValue}
                    filterValue={filterValue}
                    columnVisibility={columnVisibility}
                    divClass="table-responsive table-card mb-4"
                    tableClass="align-middle table-nowrap mb-0 table-striped"
                    theadClass="table-nowrap"
                    thClass="pt-3"
                  />
                </div>

                {isEmpty ? (
                  <div className="text-center py-5">
                    <div className="avatar-md mx-auto mb-4">
                      <div className="avatar-title bg-light text-primary rounded-circle fs-24">
                        <i
                          className={
                            hasActiveFilters
                              ? "ri-search-line"
                              : "ri-shield-check-line"
                          }
                        ></i>
                      </div>
                    </div>
                    {hasActiveFilters ? (
                      <React.Fragment>
                        <h5 className="fs-15">No matching assessments</h5>
                        <p className="text-muted mb-0">
                          No PSEA assessments match your current search and
                          filters. Clear one of them to widen the results.
                        </p>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <h5 className="fs-15">No PSEA assessments yet</h5>
                        <p className="text-muted mb-0">
                          {canCreate
                            ? "Add the first PSEA assessment to start tracking contractor compliance."
                            : "PSEA assessments will appear here once they are recorded."}
                        </p>
                      </React.Fragment>
                    )}
                  </div>
                ) : null}

                <DeleteModal
                  show={deleteModal}
                  onDeleteClick={() => handleDelete()}
                  onCloseClick={() => setDeleteModal(false)}
                  loading={deleting}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PSEAAssessments;
